import { forwardRef } from 'react';
import type { SvgIcon, SvgIconProps } from '@mui/material';
import MuiFacebookIcon from '@mui/icons-material/Facebook';
import MuiInstagramIcon from '@mui/icons-material/Instagram';
import MuiLinkedInIcon from '@mui/icons-material/LinkedIn';
import MuiXIcon from '@mui/icons-material/X';
import MuiYouTubeIcon from '@mui/icons-material/YouTube';
import {
  Bell,
  Building2,
  Calendar,
  Camera,
  ChartLine,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CircleUserRound,
  CreditCard,
  Download,
  DraftingCompass,
  EllipsisVertical,
  FilePlus,
  FileText,
  Globe,
  House,
  Image as LucideImage,
  List,
  Lock,
  LogOut,
  Mail,
  MapPin,
  Menu,
  Minus,
  Newspaper,
  Paperclip,
  Pencil,
  Plus,
  Rocket,
  Search,
  Settings,
  ShieldCheck,
  Square,
  SquareCheck,
  SquareMinus,
  Trash2,
  TrendingUp,
  TriangleAlert,
  UserPlus,
  UserX,
  Users,
  UsersRound,
  Video,
  Wallet,
  X,
} from 'lucide-react';
import type { BaseIconProps, IconComponent } from './types';

export type { BaseIconProps, IconComponent };

function muiIconComponent(Component: typeof SvgIcon) {
  return forwardRef<SVGSVGElement, SvgIconProps>((props, ref) => (
    <Component ref={ref} {...props} />
  ));
}

function iconComponent(Component: IconComponent) {
  return forwardRef<SVGSVGElement, BaseIconProps>(
    ({ size = '1rem', ...props }, ref) => (
      <Component ref={ref} size={size} {...props} />
    )
  );
}

// MUI-backed (SvgIconProps: className, color, sx, fontSize)
export const FacebookIcon = muiIconComponent(MuiFacebookIcon);
export const InstagramIcon = muiIconComponent(MuiInstagramIcon);
export const LinkedInIcon = muiIconComponent(MuiLinkedInIcon);
export const XIcon = muiIconComponent(MuiXIcon);
export const YouTubeIcon = muiIconComponent(MuiYouTubeIcon);

// lucide-backed (LucideProps: className, color, size, strokeWidth)
export const AccountIcon = iconComponent(CircleUserRound);
export const AddFileIcon = iconComponent(FilePlus);
export const AddIcon = iconComponent(Plus);
export const AddPersonIcon = iconComponent(UserPlus);
export const AdminIcon = iconComponent(ShieldCheck);
export const ArrowDownIcon = iconComponent(ChevronDown);
export const ArrowLeftIcon = iconComponent(ChevronLeft);
export const ArrowRightIcon = iconComponent(ChevronRight);
export const ArrowUpIcon = iconComponent(ChevronUp);
export const AscendingIcon = iconComponent(ChevronUp);
export const AttachmentIcon = iconComponent(Paperclip);
export const CameraIcon = iconComponent(Camera);
export const CheckIcon = iconComponent(Check);
export const CheckboxIcon = iconComponent(SquareCheck);
export const CloseIcon = iconComponent(X);
export const CompanyIcon = iconComponent(Building2);
export const DeactivateAccountIcon = iconComponent(UserX);
export const DeleteIcon = iconComponent(Trash2);
export const DescendingIcon = iconComponent(ChevronDown);
export const DownloadIcon = iconComponent(Download);
export const EditIcon = iconComponent(Pencil);
export const EmailIcon = iconComponent(Mail);
export const EventIcon = iconComponent(Calendar);
export const FileIcon = iconComponent(FileText);
export const FinancialsIcon = iconComponent(ChartLine);
export const HomeIcon = iconComponent(House);
export const ImageIcon = iconComponent(LucideImage);
export const InvestorIcon = iconComponent(Users);
export const ListIcon = iconComponent(List);
export const LocationIcon = iconComponent(MapPin);
export const LockIcon = iconComponent(Lock);
export const LogOutIcon = iconComponent(LogOut);
export const MenuIcon = iconComponent(Menu);
export const NewsIcon = iconComponent(Newspaper);
export const NotificationIcon = iconComponent(Bell);
export const NotificationsIcon = iconComponent(Bell);
export const OptionsIcon = iconComponent(EllipsisVertical);
export const PaymentIcon = iconComponent(CreditCard);
export const PdfIcon = iconComponent(FileText);
export const ProfitIcon = iconComponent(TrendingUp);
export const ProjectsIcon = iconComponent(DraftingCompass);
export const RocketIcon = iconComponent(Rocket);
export const SearchIcon = iconComponent(Search);
export const SemiCheckboxIcon = iconComponent(SquareMinus);
export const SettingsIcon = iconComponent(Settings);
export const SubtractIcon = iconComponent(Minus);
export const TeamIcon = iconComponent(UsersRound);
export const UnCheckboxIcon = iconComponent(Square);
export const VideoIcon = iconComponent(Video);
export const WalletIcon = iconComponent(Wallet);
export const WarningIcon = iconComponent(TriangleAlert);
export const WebsiteIcon = iconComponent(Globe);
